import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Button, Table } from 'react-bootstrap';
import { Utils, fetchWithAuth, formatDateTime, findOptionByValue, SENT_MESSAGE_DELIVERY_STATUS, MESSAGE_CREATED_BY_OPTIONS, MESSAGE_AUDIENCE_OPTIONS, MESSAGE_SEND_KIND_OPTIONS, exportListCsv, getBrowserTimeZone } from '../../utils';
import ListPaginationBar from '../common/ListPaginationBar';
import PushNotificationPreview from './PushNotificationPreview';
import { MdFileDownload } from "react-icons/md";
import HelpTooltip from '../common/HelpTooltip';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip as RechartsTooltip, CartesianGrid } from 'recharts';
import { toast } from 'react-toastify';

const ACTIVITY_FILTERS = [
    { key: 'all', label: 'All' },
    { key: 'sent', label: 'Sent' },
    { key: 'not_confirmed_delivery', label: 'Did Not Confirm Delivery' },
    { key: 'clicked', label: 'Clicked' },
    { key: 'not_clicked', label: 'Did Not Click' },
    { key: 'failed', label: 'Failed' },
    { key: 'unsubscribed', label: 'Unsubscribed' },
];

function formatFailureReason(activity) {
    const code = activity?.error_code?.trim();
    const message = activity?.error?.trim();
    if (code && message) return `${code}: ${message}`;
    return code || message || '—';
}

const MessageReport = ({ appData: appDataProp }) => {
    const { appId, notificationId } = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const listMessage = location.state?.listMessage;

    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activityLoading, setActivityLoading] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [appData, setAppData] = useState(appDataProp ?? null);
    const pageSize = Math.max(1, Number(Utils.pagination?.defaultPageSize));
    const initialReportLoadedRef = useRef(false);

    const activityPage = Math.max(1, parseInt(searchParams.get('activity_page') || '1', 10));
    const activityFilter = searchParams.get('activity_filter') || 'all';
    const activityLimit = Math.min(
        200,
        Math.max(1, parseInt(searchParams.get('activity_limit') || String(pageSize), 10))
    );

    useEffect(() => {
        if (appDataProp) setAppData(appDataProp);
    }, [appDataProp]);

    useEffect(() => {
        if (!appId || appDataProp) return;
        let cancelled = false;
        (async () => {
            try {
                const response = await fetchWithAuth(Utils.baseurl + 'getbyid/application', {
                    method: 'POST',
                    body: JSON.stringify({ id: appId }),
                });
                const result = await response.json();
                if (!cancelled && result.status) setAppData(result.data);
            } catch (e) {
                console.error('Error fetching application:', e);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [appId, appDataProp]);

    useEffect(() => {
        if (!notificationId) return;
        const p = new URLSearchParams(searchParams);
        let changed = false;
        if (!p.get('activity_page')) {
            p.set('activity_page', '1');
            changed = true;
        }
        if (!p.get('activity_filter')) {
            p.set('activity_filter', 'all');
            changed = true;
        }
        if (!p.get('activity_limit')) {
            p.set('activity_limit', String(pageSize));
            changed = true;
        }
        if (changed) setSearchParams(p, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [notificationId]);

    useEffect(() => {
        initialReportLoadedRef.current = false;
    }, [notificationId]);

    const handleBack = () => {
        const ret = searchParams.get('returnTo');
        if (ret) {
            try {
                const path = decodeURIComponent(ret);
                if (path.startsWith('/')) {
                    navigate(path);
                    return;
                }
            } catch {
                /* ignore */
            }
        }
        navigate(`/apps/${appId}/sent-messages`);
    };

    const handleExportActivity = async () => {
        if (isExporting || !notificationId) return;
        setIsExporting(true);
        try {
            const { status, message } = await exportListCsv(
                "get/message-report",
                {
                    notification_id: notificationId,
                    activity_filter: activityFilter,
                    timezone: getBrowserTimeZone() || undefined,
                },
                "audience-activity.csv"
            );
            if (!status) {
                toast.error(message);
                return;
            }
            if (message) toast.info(message);
        } catch (error) {
            console.error(error);
            toast.error("Export failed");
        } finally {
            setIsExporting(false);
        }
    };

    const fetchReportData = useCallback(async () => {
        if (!notificationId) {
            setLoading(false);
            return;
        }

        const skipFullPage = initialReportLoadedRef.current;
        if (skipFullPage) setActivityLoading(true);
        else setLoading(true);

        try {
            const qs = new URLSearchParams({
                notification_id: notificationId,
                activity_page: String(activityPage),
                activity_limit: String(activityLimit),
                activity_filter: activityFilter,
            });
            const response = await fetchWithAuth(
                `${Utils.baseurl}get/message-report?${qs.toString()}`,
                { method: 'GET' }
            );
            const result = await response.json();
            if (result.status) {
                setReportData(result.data);
                initialReportLoadedRef.current = true;
            }
        } catch (error) {
            console.error('Error fetching report data:', error);
        } finally {
            setLoading(false);
            setActivityLoading(false);
        }
    }, [notificationId, activityPage, activityFilter, activityLimit]);

    useEffect(() => {
        fetchReportData();
    }, [fetchReportData]);

    if (!notificationId) {
        return (
            <div className="message-report-container">
                <div className="text-center p-5">Invalid report link.</div>
            </div>
        );
    }

    // Use dynamic data from API or fallback to list navigation state
    const stats = {
        delivered: reportData?.statistics?.total_delivered || 0,
        confirmedDelivered: reportData?.statistics?.total_confirmed_delivered || 0,
        clicks: reportData?.statistics?.total_clicks || 0,
        clickThroughRate: reportData?.statistics?.ctr || '0.00%',
        confirmedCTR: reportData?.statistics?.confirmed_ctr || '0.00%',
        influencedOpens: reportData?.statistics?.influenced_rate || '0.00%',
        influencedUnique: reportData?.statistics?.total_influenced_opens || 0,
        totalSent: reportData?.statistics?.total_sent || 0,
        totalFailed: reportData?.statistics?.total_failed || 0,
    };

    const deliveryStats = reportData?.delivery_stats || {
        audience: stats.totalSent,
        sent: stats.totalSent,
        delivered: stats.delivered,
        confirmed: stats.confirmedDelivered,
        unsubscribed: 0,
        error: stats.totalFailed,
        remaining: 0,
        canceled: 0,
    };


    const funnelData = [
        { name: 'Sent', count: stats.totalSent },
        { name: 'Delivered', count: stats.delivered },
        { name: 'Confirmed', count: stats.confirmedDelivered },
        { name: 'Clicked', count: stats.clicks },
        { name: 'Influenced Opens', count: stats.influencedUnique },
    ];

    const platformStats = reportData?.platform_stats || {};
    const audienceActivity = reportData?.audience_activity || [];
    const activityMeta = reportData?.audience_activity_meta;
    const showFailureReason =
        activityFilter === 'failed' ||
        audienceActivity.some((activity) => activity.error || activity.error_code);
    const activityColumnCount = showFailureReason ? 7 : 6;
    const activityTotal = Number(activityMeta?.total ?? 0);
    const totalActivityPages = Math.max(
        1,
        Number(activityMeta?.total_pages) || Math.ceil(activityTotal / activityLimit) || 1
    );
    const messageContent = reportData?.message || {
        title: listMessage?.name || listMessage?.title,
        body: listMessage?.title,
    };
    const statusOpt = findOptionByValue(
        SENT_MESSAGE_DELIVERY_STATUS,
        listMessage?.status ?? reportData?.status
    );
    const createdFromOpt = findOptionByValue(
        MESSAGE_CREATED_BY_OPTIONS,
        reportData?.sent_from ?? listMessage?.sentFrom ?? listMessage?.created_by
    );
    const audienceOpt = findOptionByValue(
        MESSAGE_AUDIENCE_OPTIONS,
        reportData?.audience_type ?? listMessage?.audience_type
    );
    const sendKindOpt = findOptionByValue(
        MESSAGE_SEND_KIND_OPTIONS,
        reportData?.send_kind ?? listMessage?.sendKind
    );
    const segmentNames = (reportData?.audience_segments?.length
        ? reportData.audience_segments.map((s) => s.name)
        : listMessage?.segment_names || []
    ).filter(Boolean);

    const handleActivityFilterClick = (key) => {
        setSearchParams(
            (prev) => {
                const p = new URLSearchParams(prev);
                p.set('activity_filter', key);
                p.set('activity_page', '1');
                return p;
            },
            { replace: true }
        );
    };

    const goActivityPage = (p) => {
        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                next.set('activity_page', String(p));
                return next;
            },
            { replace: true }
        );
    };

    return (
        <div className="message-report-container">
            {/* Header */}
            <div className="report-header">
                <div className="report-breadcrumb">
                    <span className="breadcrumb-link" onClick={handleBack}>
                        {appData?.organization?.oganization_name || 'Organization'}
                    </span>
                    <span className="breadcrumb-separator">&gt;</span>
                    <span className="breadcrumb-link" onClick={handleBack}>
                        {appData?.application_name || 'Application'}
                    </span>
                    <span className="breadcrumb-separator">&gt;</span>
                    <span className="breadcrumb-link" onClick={handleBack}>Messages</span>
                    <span className="breadcrumb-separator">&gt;</span>
                    <span className="breadcrumb-current">Push</span>
                </div>
                <div className="report-actions">
                    <Button variant="outline-secondary" size="sm" onClick={handleBack}>
                        ← Back
                    </Button>
                </div>
            </div>

            {/* Message Title */}
            <div className="message-title-section">
                <h2 className="message-title">
                    {listMessage?.name || listMessage?.title || reportData?.message?.title || 'Push Notification'}
                </h2>
            </div>
            <div className="message-meta">
                <span className="message-meta-started">
                    Started sending at:{' '}
                    {formatDateTime(reportData?.created_at || listMessage?.sent_at || listMessage?.created_at, {
                        seconds: true,
                    })}
                </span>
                {statusOpt ? (
                    <span className={statusOpt.badgeClass}>{statusOpt.label}</span>
                ) : null}
                {audienceOpt ? (
                    <span className={audienceOpt.badgeClass}>
                        {segmentNames.length
                            ? `${audienceOpt.label}: ${segmentNames.join(', ')}`
                            : audienceOpt.label}
                    </span>
                ) : null}
                {sendKindOpt ? (
                    <span className={sendKindOpt.badgeClass}>{sendKindOpt.label}</span>
                ) : null}
                {createdFromOpt ? (
                    <span className={createdFromOpt.badgeClass}>{createdFromOpt.label}</span>
                ) : null}
            </div>

            <div className="stats-cards">
                <div className="stat-card">
                    <div className="stat-label">
                        Delivered
                        <HelpTooltip text="Pushes the server received a successful delivery status for, out of everyone it was sent to. (confirmed) is the subset where the app itself called back to confirm it actually arrived on the device — that callback only fires if the app is awake, so a High-priority push reports more confirmed deliveries." />
                    </div>
                    <div className="stat-value">{stats.delivered}</div>
                    <div className="stat-sublabel">({stats.confirmedDelivered} confirmed)</div>
                </div>
                <div className="stat-card">
                    <div className="stat-label">
                        Click Through Rate
                        <HelpTooltip text="Clicks ÷ Delivered × 100. How many of the pushes that were delivered were then clicked." />
                    </div>
                    <div className="stat-value">{stats.clickThroughRate}</div>
                    <div className="stat-sublabel">({stats.clicks} clicks)</div>
                </div>
                <div className="stat-card">
                    <div className="stat-label">
                        Confirmed CTR
                        <HelpTooltip text="Clicks ÷ Confirmed Delivered × 100 — the same click rate, but measured only against deliveries the app actually confirmed, instead of every delivery the server reported." />
                    </div>
                    <div className="stat-value">{stats.confirmedCTR}</div>
                    <div className="stat-sublabel">({stats.clicks} clicks)</div>
                </div>
                <div className="stat-card">
                    <div className="stat-label">
                        Influenced Opens
                        <HelpTooltip text="Influenced Opens ÷ Total Sent × 100. App opens attributed to this push even when the user didn't directly tap the notification — e.g. they saw it and opened the app on their own shortly after." />
                    </div>
                    <div className="stat-value">{stats.influencedOpens}</div>
                    <div className="stat-sublabel">({stats.influencedUnique} unique)</div>
                </div>
            </div>

            {/* Message Statistics — a real funnel chart built from the same aggregate counts as
                the stat cards/outcomes table above (Sent -> Delivered -> Confirmed -> Clicked,
                plus Influenced Opens). The old version here was a hardcoded SVG line with no
                data behind it and non-functional filter dropdowns — replaced with a real bar
                chart instead of being removed. A per-day trend line isn't possible yet since the
                backend only returns totals for the whole send, not a daily breakdown. */}
            <div className="chart-section">
                <div className="chart-header">
                    <h3>Message Statistics</h3>
                </div>
                <p className="chart-note">Counts across the full send so far.</p>
                <ResponsiveContainer width="100%" height={240}>
                    <LineChart data={funnelData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                        <RechartsTooltip />
                        <Line type="monotone" dataKey="count" stroke="#6c5ce7" strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                    </LineChart>
                </ResponsiveContainer>
            </div>

            {/* Outcomes Table — real, backend-computed counts only. The old version here had a
                Direct/Influenced/Total breakdown that was never wired to real data (a "Sessions"
                metric the backend doesn't track, and N/A/0 placeholders) — simplified instead of
                left showing numbers that don't mean anything. */}
            <div className="outcomes-section">
                <h3 className="mb-3">Outcomes</h3>
                <Table className="outcomes-table">
                    <thead>
                        <tr>
                            <th>Outcome</th>
                            <th>Count</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>Clicks</td>
                            <td>{stats.clicks}</td>
                        </tr>
                        <tr>
                            <td>Confirmed Delivery</td>
                            <td>{stats.confirmedDelivered}</td>
                        </tr>
                        <tr>
                            <td>Influenced Opens</td>
                            <td>{stats.influencedUnique}</td>
                        </tr>
                    </tbody>
                </Table>
            </div>

            {/* Statistics Row */}
            <div className="statistics-row">
                <div className="delivery-stats">
                    <h4>Delivery Statistics</h4>
                    <div className="progress-bar-container">
                        <div className="progress-bar-fill" style={{ width: deliveryStats.audience > 0 ? `${(deliveryStats.delivered / deliveryStats.audience) * 100}%` : '0%' }}></div>
                    </div>
                    <ul className="stats-list">
                        <li><span className="dot audience"></span> Audience <span className="value">{deliveryStats.audience}</span></li>
                        <li><span className="dot sent"></span> Sent <span className="value">{deliveryStats.sent}</span></li>
                        <li><span className="dot delivered"></span> Delivered <span className="value">{deliveryStats.delivered}</span></li>
                        <li><span className="dot confirmed"></span> Confirmed <span className="value">{deliveryStats.confirmed}</span></li>
                        <li><span className="dot error"></span> Error <span className="value">{deliveryStats.error}</span></li>
                    </ul>
                </div>
                <div className="platform-stats">
                    <h4>Platform Statistics</h4>
                    <p className="platform-label">Total Delivered</p>
                    <div className="progress-bar-container">
                        <div className="progress-bar-fill android" style={{ width: '100%' }}></div>
                    </div>
                    <ul className="stats-list">
                        {Object.keys(platformStats).length > 0 ? (
                            Object.entries(platformStats).map(([platform, data]) => (
                                <li key={platform}>
                                    <span className={`dot ${platform.toLowerCase()}`}></span>
                                    {platform === 'Android' ? 'Google Android' : platform}
                                    <span className="value">{data.delivered}</span>
                                </li>
                            ))
                        ) : (
                            <li><span className="dot android"></span> Google Android <span className="value">{stats.delivered}</span></li>
                        )}
                    </ul>
                </div>
            </div>

            {/* Audience Activity */}
            <div className="audience-activity">
                <div className="activity-header">
                    <h4>Audience Activity</h4>
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        type="button"
                        title="Export CSV for applied filters"
                        onClick={handleExportActivity}
                        disabled={isExporting}
                    >
                        <MdFileDownload />
                        {isExporting ? "Exporting…" : "Export"}
                    </Button>
                </div>
                <div className="activity-filters" role="toolbar" aria-label="Audience activity filters">
                    {ACTIVITY_FILTERS.map(({ key, label }) => (
                        <button
                            key={key}
                            type="button"
                            className={`filter-chip ${activityFilter === key ? 'filter-chip--active' : ''}`}
                            onClick={() => handleActivityFilterClick(key)}
                            aria-pressed={activityFilter === key}
                        >
                            {label}
                        </button>
                    ))}
                </div>
                <div className={`activity-table-wrap ${activityLoading ? 'activity-table-wrap--loading' : ''}`}>
                    <Table className="activity-table">
                        <thead>
                            <tr>
                                <th>External ID</th>
                                <th>Notification ID</th>
                                <th>Subscription ID</th>
                                <th>Device</th>
                                <th>Delivery Status</th>
                                {showFailureReason ? <th>Failure reason</th> : null}
                                <th>Sent</th>
                            </tr>
                        </thead>
                        <tbody>
                            {audienceActivity.length > 0 ? (
                                audienceActivity.map((activity) => {
                                    const ds = (activity.delivery_status || '').toLowerCase();
                                    const badgeClass =
                                        ds === 'delivered'
                                            ? 'subscribed'
                                            : ds === 'failed'
                                                ? 'failed'
                                                : 'pending';
                                    return (
                                        <tr key={activity.id ?? `${activity.subscription_id}-${activity.sent_at}`}>
                                            <td>{activity.external_id || '-'}</td>
                                            <td className="truncate">
                                                {activity.notification_id || notificationId || '-'}
                                            </td>
                                            <td className="truncate">{activity.subscription_id || '-'}</td>
                                            <td>
                                                <span className="device-badge">
                                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="#4CAF50">
                                                        <path d="M17.6 9.48l1.84-3.18c.16-.31.04-.69-.26-.85-.29-.15-.65-.06-.83.22l-1.88 3.24c-1.4-.59-2.96-.92-4.47-.92s-3.07.33-4.47.92L5.65 5.67c-.19-.29-.58-.38-.87-.2-.28.18-.37.54-.22.83L6.4 9.48C3.3 11.25 1.28 14.44 1 18h22c-.28-3.56-2.3-6.75-5.4-8.52zM7 15.25c-.69 0-1.25-.56-1.25-1.25s.56-1.25 1.25-1.25 1.25.56 1.25 1.25-.56 1.25-1.25 1.25zm10 0c-.69 0-1.25-.56-1.25-1.25s.56-1.25 1.25-1.25 1.25.56 1.25 1.25-.56 1.25-1.25 1.25z" />
                                                    </svg>
                                                    {activity.device_model || activity.device_type || 'Android Device'}
                                                </span>
                                            </td>
                                            <td>
                                                <span className={`subscription-badge ${badgeClass}`}>
                                                    {activity.delivery_status || 'Pending'}
                                                </span>
                                            </td>
                                            {showFailureReason ? (
                                                <td className="truncate failure-reason-cell" title={formatFailureReason(activity)}>
                                                    {formatFailureReason(activity)}
                                                </td>
                                            ) : null}
                                            <td>{formatDateTime(activity.sent_at)}</td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan={activityColumnCount} style={{ textAlign: 'center', padding: '24px', color: '#6c757d' }}>
                                        {activityLoading || (loading && !reportData) ? 'Loading…' : 'No activity for this filter.'}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </div>
                <ListPaginationBar
                    page={activityPage}
                    setPage={goActivityPage}
                    total={activityTotal}
                    pageSize={activityLimit}
                    totalPages={totalActivityPages}
                    ariaLabel="Audience activity pagination"
                    className="px-0 py-2 flex-wrap gap-2 activity-pagination"
                    loading={activityLoading}
                />
                <p className="activity-note">
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="#6c757d">
                        <path d="M8 0a8 8 0 100 16A8 8 0 008 0zm0 14A6 6 0 118 2a6 6 0 010 12zm-.5-9h1v5h-1V5zm0 6h1v1h-1v-1z" />
                    </svg>
                    Activity is available for 30 days from the time the message was sent
                </p>
            </div>

            {/* Message Settings */}
            <div className="message-settings-section">
                <h4>Message Settings</h4>
                <div className="settings-content">
                    <div className="settings-details">
                        {/* Audience */}
                        <div className="settings-group-header">Audience</div>
                        <div className="settings-row">
                            <span className="settings-label">Total number of recipients (rows)</span>
                            <span className="settings-value">{stats.totalSent}</span>
                        </div>

                        {/* Schedule */}
                        <div className="settings-group-header">Schedule</div>
                        <div className="settings-row">
                            <span className="settings-label">Start sending</span>
                            <span className="settings-value">{formatDateTime(reportData?.created_at || listMessage?.sent_at || listMessage?.created_at, { seconds: true })}</span>
                        </div>
                        <div className="settings-row">
                            <span className="settings-label">Delivery time</span>
                            <span className="settings-value">Deliver immediately</span>
                        </div>

                        {/* Message */}
                        <div className="settings-group-header">Message</div>
                        <div className="settings-row message-row">
                            <span className="settings-label">Message</span>
                            <span className="settings-value"></span>
                        </div>
                        <div className="settings-row message-content-row">
                            <span className="settings-label language-label">
                                <span className="language-indicator"></span>
                                Any/English
                            </span>
                            <span className="settings-value message-text">
                                {messageContent?.title || messageContent?.body || listMessage?.name || listMessage?.title}
                            </span>
                        </div>

                        {/* Google Android */}
                        <div className="settings-group-header android-header">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="#3DDC84">
                                <path d="M17.6 9.48l1.84-3.18c.16-.31.04-.69-.26-.85-.29-.15-.65-.06-.83.22l-1.88 3.24c-1.4-.59-2.96-.92-4.47-.92s-3.07.33-4.47.92L5.65 5.67c-.19-.29-.58-.38-.87-.2-.28.18-.37.54-.22.83L6.4 9.48C3.3 11.25 1.28 14.44 1 18h22c-.28-3.56-2.3-6.75-5.4-8.52zM7 15.25c-.69 0-1.25-.56-1.25-1.25s.56-1.25 1.25-1.25 1.25.56 1.25 1.25-.56 1.25-1.25 1.25zm10 0c-.69 0-1.25-.56-1.25-1.25s.56-1.25 1.25-1.25 1.25.56 1.25 1.25-.56 1.25-1.25 1.25z" />
                            </svg>
                            Google Android
                        </div>
                        <div className="settings-row">
                            <span className="settings-label">Sound</span>
                            <span className="settings-value">Default</span>
                        </div>
                        <div className="settings-row">
                            <span className="settings-label">Lockscreen Visibility</span>
                            <span className="settings-value">Public - Always show the contents on the lockscreen.</span>
                        </div>
                        <div className="settings-row">
                            <span className="settings-label">Channel Mode</span>
                            <span className="settings-value">Default</span>
                        </div>

                        {/* Advanced Settings */}
                        <div className="settings-group-header">Advanced Settings</div>
                        <div className="settings-row">
                            <span className="settings-label">Priority</span>
                            <span className="settings-value">Normal</span>
                        </div>
                        <div className="settings-row">
                            <span className="settings-label">Time To Live</span>
                            <span className="settings-value">3 days</span>
                        </div>

                        {/* Additional Data */}
                        <div className="settings-group-header">Additional Data</div>
                        {messageContent?.data && Object.keys(messageContent.data).filter(key => !['title', 'message', 'body'].includes(key)).length > 0 ? (
                            Object.entries(messageContent.data).filter(([key]) => !['title', 'message', 'body'].includes(key)).map(([key, value]) => (
                                <div className="settings-row" key={key}>
                                    <span className="settings-label">Key: {key}</span>
                                    <span className="settings-value">Value: {typeof value === 'object' ? JSON.stringify(value) : value}</span>
                                </div>
                            ))
                        ) : (
                            <div className="settings-row">
                                <span className="settings-label">Key: redirect_action</span>
                                <span className="settings-value">Value: N/A</span>
                            </div>
                        )}
                    </div>

                    {/* Phone Preview - same shared component as the compose page and the Automation
                        Push node, so all three look identical instead of three near-duplicates. */}
                    <div className="phone-preview">
                        <PushNotificationPreview
                            appName={appData?.application_name || 'App'}
                            title={messageContent?.title || ''}
                            message={messageContent?.body || listMessage?.name || listMessage?.title}
                            imageUrl={messageContent?.data?.image}
                        />
                        <p className="preview-label">Google Android</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MessageReport;
